import { motion } from "framer-motion";
import { Trophy, Calendar, DollarSign } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";

const FAKE_WINS = [
    { player: "De'Aaron Fox", line: "OVER 24.5 PTS", payout: "$1,250", date: "Apr 12", multiplier: "25x", sport: "NBA" },
    { player: "Patrick Mahomes", line: "OVER 268.5 PASS YDS", payout: "$842", date: "Apr 14", multiplier: "10x", sport: "NFL" },
    { player: "Aaron Judge", line: "OVER 1.5 TOTAL BASES", payout: "$3,400", date: "Apr 15", multiplier: "100x", sport: "MLB" },
    { player: "Connor McDavid", line: "OVER 1.5 ASSISTS", payout: "$520", date: "Apr 16", multiplier: "5x", sport: "NHL" },
    { player: "Luka Don\u010di\u0107", line: "OVER 30.5 PTS+REB+AST", payout: "$2,100", date: "Apr 18", multiplier: "21x", sport: "NBA" },
    { player: "Caitlin Clark", line: "OVER 6.5 ASSISTS", payout: "$640", date: "Apr 19", multiplier: "6.4x", sport: "WNBA" },
    { player: "Lionel Messi", line: "OVER 2.5 SHOTS", payout: "$910", date: "Apr 20", multiplier: "9x", sport: "MLS" },
    { player: "Shohei Ohtani", line: "OVER 0.5 HRs", payout: "$1,750", date: "Apr 22", multiplier: "17.5x", sport: "MLB" },
];

const auraByMult = (multStr) => {
    const n = parseFloat(multStr);
    if (isNaN(n)) return "#22c55e";
    if (n >= 50) return "#ef4444";
    if (n >= 20) return "#f97316";
    if (n >= 10) return "#a855f7";
    if (n >= 5) return "#3b82f6";
    return "#22c55e";
};

export const RecentWinnings = () => {
    return (
        <section className="relative pp-bg-0 py-16 sm:py-20" data-testid="recent-winnings-section">
            <div className="max-w-6xl mx-auto px-4 sm:px-6">
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="flex items-end justify-between gap-4 mb-8"
                >
                    <div>
                        <p className="text-xs font-mono uppercase text-[#22c55e] tracking-widest">Wall of Wins</p>
                        <h2 className="font-display text-3xl sm:text-4xl tracking-wider text-white">RECENT PRIZEPICKS WINNINGS</h2>
                        <p className="mt-1 text-sm text-[#8b8ba0] max-w-xl">Snapshot of recent winning slips. For entertainment—past wins don’t guarantee future cashouts.</p>
                    </div>
                </motion.div>

                <Carousel
                    opts={{ align: "start", loop: true }}
                    className="w-full"
                    data-testid="recent-winnings-carousel"
                >
                    <CarouselContent className="-ml-2">
                        {FAKE_WINS.map((w, i) => {
                            const c = auraByMult(w.multiplier);
                            return (
                                <CarouselItem key={i} className="pl-2 basis-4/5 sm:basis-1/2 lg:basis-1/3">
                                    <motion.div
                                        whileHover={{ y: -3 }}
                                        className="relative pp-card-bg rounded-xl border border-[#242436] p-4 h-full"
                                        style={{ boxShadow: `0 0 0 1px ${c}33, 0 0 24px ${c}22` }}
                                        data-testid={`win-card-${i}`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className="inline-flex items-center gap-1.5 text-xs font-mono uppercase text-[#b7b7c6]">
                                                <Calendar className="w-3.5 h-3.5" /> {w.date}
                                            </span>
                                            <span className="font-mono text-xs px-2 py-0.5 rounded-md" style={{ color: c, border: `1px solid ${c}55` }}>{w.sport}</span>
                                        </div>
                                        <div className="mt-3">
                                            <p className="font-display text-xl tracking-wider text-white">{w.player}</p>
                                            <p className="text-sm text-[#b7b7c6]">{w.line}</p>
                                        </div>
                                        <div className="mt-4 flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <Trophy className="w-4 h-4 text-[#22c55e]" />
                                                <span className="font-display text-2xl tracking-wider" style={{ color: c }}>{w.multiplier}</span>
                                            </div>
                                            <span className="inline-flex items-center gap-1 font-display text-xl tracking-wider text-white">
                                                <DollarSign className="w-4 h-4 text-[#22c55e]" />
                                                {w.payout.replace("$", "")}
                                            </span>
                                        </div>
                                    </motion.div>
                                </CarouselItem>
                            );
                        })}
                    </CarouselContent>
                    <CarouselPrevious className="hidden sm:flex pp-bg-1 border-[#242436] text-white hover:bg-[#1b1b26]" data-testid="winnings-prev" />
                    <CarouselNext className="hidden sm:flex pp-bg-1 border-[#242436] text-white hover:bg-[#1b1b26]" data-testid="winnings-next" />
                </Carousel>
            </div>
        </section>
    );
};
